import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage, privateMediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AppContext";
import BeforeAfter from "@/components/site/BeforeAfter";
import { AdminTitle } from "./AdminLayout";

const EMPTY = { title: "", description: "", treatment_id: null, before_media_id: null, after_media_id: null, before_alt: "", after_alt: "", before_crop: { x: 50, y: 50, zoom: 1 }, after_crop: { x: 50, y: 50, zoom: 1 }, consent_reference: "", consent_confirmed: false, order: 0 };
const ST = { draft: "Bozza", published: "Pubblicato", hidden: "Nascosto", archived: "Archiviato" };

// Mirrors server crop_box: object-fit cover + object-position x/y + scale(zoom) around the same origin.
function CropPreview({ mediaId, crop }) {
  if (!mediaId) return <div className="flex aspect-[4/5] items-center justify-center rounded-xl bg-[var(--ivory)] text-xs">Nessuna immagine</div>;
  return (
    <div className="aspect-[4/5] overflow-hidden rounded-xl bg-[var(--ivory)]">
      <img src={privateMediaUrl(mediaId)} alt="" className="h-full w-full object-cover" style={{ objectPosition: `${crop.x}% ${crop.y}%`, transform: `scale(${crop.zoom})`, transformOrigin: `${crop.x}% ${crop.y}%` }} />
    </div>
  );
}

function ImageSlot({ label, which, data, setData }) {
  const crop = data[`${which}_crop`];
  const setCrop = (k, v) => setData({ ...data, [`${which}_crop`]: { ...crop, [k]: Number(v) } });
  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file); fd.append("purpose", "gallery");
    try { const r = await api.post("/admin/media", fd); setData({ ...data, [`${which}_media_id`]: r.data.id }); toast.success("Originale caricato (privato)"); } catch (err) { toast.error(errorMessage(err)); }
  };
  return (
    <div className="grid gap-3">
      <p className="eyebrow">{label}</p>
      <CropPreview mediaId={data[`${which}_media_id`]} crop={crop} />
      <input type="file" accept="image/jpeg,image/png,image/webp" onChange={upload} data-testid={`gallery-upload-${which}`} />
      {[["x", "Posizione orizzontale", 0, 100, 1], ["y", "Posizione verticale", 0, 100, 1], ["zoom", "Zoom", 1, 3, 0.05]].map(([k, l, min, max, step]) => (
        <label key={k} className="text-xs">{l}: {crop[k]}<input type="range" min={min} max={max} step={step} value={crop[k]} onChange={(e) => setCrop(k, e.target.value)} className="w-full accent-[var(--petrol)]" data-testid={`gallery-${which}-${k}`} /></label>
      ))}
      <label className="text-sm">Testo alternativo<input className="field mt-1" maxLength={200} value={data[`${which}_alt`]} onChange={(e) => setData({ ...data, [`${which}_alt`]: e.target.value })} data-testid={`gallery-${which}-alt`} /></label>
    </div>
  );
}

function CaseEditor({ item, treatments, onClose }) {
  const { can } = useAuth();
  const [data, setData] = useState(() => (item ? Object.fromEntries(Object.keys(EMPTY).map((k) => [k, item[k] ?? EMPTY[k]])) : EMPTY));
  const [preview, setPreview] = useState(false);
  const save = async () => {
    try {
      if (item) await api.put(`/admin/gallery/${item.id}`, data); else await api.post("/admin/gallery", data);
      toast.success(item?.status === "published" ? "Salvato: il caso è tornato in bozza, ripubblicalo dopo l'anteprima" : "Salvato in bozza"); onClose(true);
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const uploadConsent = async (e) => {
    const fd = new FormData();
    fd.append("file", e.target.files[0]); fd.append("purpose", "consent");
    try { const r = await api.post("/admin/media", fd); await api.post(`/admin/gallery/${item.id}/consent-document`, { media_id: r.data.id }); toast.success("Documento allegato (privato)"); } catch (err) { toast.error(errorMessage(err)); }
  };
  return (
    <div className="card-soft grid gap-6 p-6" data-testid="gallery-editor">
      <h2 className="text-2xl">{item ? "Modifica caso" : "Nuovo caso"}</h2>
      <div className="grid gap-4 md:grid-cols-2">
        <label className="text-sm">Titolo<input className="field mt-1" value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })} data-testid="gallery-title" /></label>
        <label className="text-sm">Trattamento<select className="field mt-1" value={data.treatment_id || ""} onChange={(e) => setData({ ...data, treatment_id: e.target.value || null })} data-testid="gallery-treatment"><option value="">—</option>{treatments.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></label>
        <label className="text-sm md:col-span-2">Descrizione<textarea rows={2} className="field mt-1" value={data.description} onChange={(e) => setData({ ...data, description: e.target.value })} data-testid="gallery-description" /></label>
      </div>
      <div className="grid gap-8 md:grid-cols-2">
        <ImageSlot label="Prima" which="before" data={data} setData={setData} />
        <ImageSlot label="Dopo" which="after" data={data} setData={setData} />
      </div>
      <div className="grid gap-4 rounded-xl bg-[var(--ivory-soft)] p-4 md:grid-cols-2">
        <label className="text-sm">Riferimento interno autorizzazione (es. n. protocollo, senza dati personali)<input className="field mt-1" maxLength={120} value={data.consent_reference} onChange={(e) => setData({ ...data, consent_reference: e.target.value })} data-testid="gallery-consent-ref" /></label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={data.consent_confirmed} onChange={(e) => setData({ ...data, consent_confirmed: e.target.checked })} data-testid="gallery-consent-confirmed" /> Confermo che l'autorizzazione scritta alla pubblicazione è archiviata e che le immagini non contengono elementi identificativi.</label>
        {item && can("consent.manage") && <label className="text-sm">Documento di autorizzazione (PDF/immagine, solo proprietario){item.has_consent_document && " · già allegato"}<input type="file" accept="application/pdf,image/jpeg,image/png" className="mt-1 block" onChange={uploadConsent} data-testid="gallery-consent-file" /></label>}
      </div>
      <div className="flex flex-wrap gap-3">
        <button className="btn-outline" onClick={() => setPreview(!preview)} data-testid="gallery-preview-toggle">{preview ? "Chiudi anteprima" : "Anteprima confronto"}</button>
        <button className="btn-primary" onClick={save} data-testid="gallery-save">Salva bozza</button>
        <button className="btn-outline" onClick={() => onClose(false)}>Chiudi</button>
      </div>
      {preview && data.before_media_id && data.after_media_id && (
        <div className="max-w-sm"><p className="mb-2 text-xs text-[var(--muted-ink)]">Anteprima privata (ritaglio applicato come in pubblicazione)</p>
          <PreviewCompare data={data} /></div>
      )}
    </div>
  );
}

function PreviewCompare({ data }) {
  const [urls, setUrls] = useState(null);
  useEffect(() => {
    let alive = true;
    const crop = async (id, c) => {
      const img = new Image();
      img.src = privateMediaUrl(id);
      await img.decode();
      const r = 4 / 5; const w = img.naturalWidth; const h = img.naturalHeight;
      const [bw, bh] = w / h < r ? [w, w / r] : [h * r, h];
      const left = (w - bw) * c.x / 100; const top = (h - bh) * c.y / 100;
      const px = left + bw * c.x / 100; const py = top + bh * c.y / 100;
      const zw = bw / c.zoom; const zh = bh / c.zoom;
      const cv = document.createElement("canvas"); cv.width = 800; cv.height = 1000;
      cv.getContext("2d").drawImage(img, px - zw * c.x / 100, py - zh * c.y / 100, zw, zh, 0, 0, 800, 1000);
      return cv.toDataURL("image/jpeg", 0.85);
    };
    Promise.all([crop(data.before_media_id, data.before_crop), crop(data.after_media_id, data.after_crop)]).then(([b, a]) => alive && setUrls({ b, a }));
    return () => { alive = false; };
  }, [data]);
  if (!urls) return <p className="text-sm">Generazione anteprima…</p>;
  return <BeforeAfter beforeSrc={urls.b} afterSrc={urls.a} beforeAlt={data.before_alt} afterAlt={data.after_alt} testid="gallery-admin-preview" />;
}

export default function GalleryAdmin() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [editing, setEditing] = useState(undefined);
  const load = () => api.get("/admin/gallery").then((r) => setItems(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => {
    load();
    api.get("/admin/treatments").then((r) => setTreatments(r.data)).catch(() => {});
  }, []);
  const status = async (id, s) => { try { await api.post(`/admin/gallery/${id}/status`, { status: s }); toast.success(ST[s]); load(); } catch (e) { toast.error(errorMessage(e)); } };
  const move = async (i, d) => { const ids = items.map((x) => x.id); [ids[i], ids[i + d]] = [ids[i + d], ids[i]]; await api.post("/admin/gallery/reorder", { ids }); load(); };
  if (editing !== undefined) return <CaseEditor item={editing} treatments={treatments} onClose={(s) => { setEditing(undefined); if (s) load(); }} />;
  const w = can("gallery.write");
  return (
    <>
      <AdminTitle title="Galleria prima/dopo">{w && <button className="btn-primary !py-2 text-sm" onClick={() => setEditing(null)} data-testid="gallery-new">Nuovo caso</button>}</AdminTitle>
      <p className="mb-6 max-w-3xl text-sm text-[var(--muted-ink)]">Gli originali restano privati. Alla pubblicazione il server genera versioni ritagliate e ricodificate senza metadati EXIF; solo queste sono pubbliche. Modificare un caso pubblicato lo riporta in bozza.</p>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {items.map((c, i) => (
          <div key={c.id} className="card-soft p-4" data-testid={`gallery-admin-case-${c.id}`}>
            <div className="grid grid-cols-2 gap-2"><CropPreview mediaId={c.before_media_id} crop={c.before_crop} /><CropPreview mediaId={c.after_media_id} crop={c.after_crop} /></div>
            <p className="mt-3 font-serif text-xl">{c.title}</p>
            <p className="text-xs text-[var(--muted-ink)]">{ST[c.status]} · autorizzazione: {c.consent_reference || "mancante"}</p>
            {w && <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <button className="underline" onClick={() => setEditing(c)} data-testid={`gallery-edit-${c.id}`}>Modifica</button>
              {c.status !== "published" ? <button className="underline" onClick={() => status(c.id, "published")} data-testid={`gallery-publish-${c.id}`}>Pubblica</button> : <button className="underline" onClick={() => status(c.id, "hidden")} data-testid={`gallery-hide-${c.id}`}>Nascondi</button>}
              {c.status !== "archived" && <button className="text-red-700 underline" onClick={() => status(c.id, "archived")}>Archivia</button>}
              <button disabled={i === 0} onClick={() => move(i, -1)}>↑</button><button disabled={i === items.length - 1} onClick={() => move(i, 1)}>↓</button>
            </div>}
          </div>
        ))}
      </div>
    </>
  );
}
